import legalManifest from './legal-manifest.json' with { type: 'json' };

const LAST_UPDATED_DATE = new Date(legalManifest.termsUpdatedAt);
const EFFECTIVE_DATE = `${LAST_UPDATED_DATE.getUTCMonth() + 1}/${LAST_UPDATED_DATE.getUTCDate()}/${LAST_UPDATED_DATE.getUTCFullYear()}`;
const LAST_UPDATED = EFFECTIVE_DATE;

// There is no header, because the app itself adds a header
export const TERMS_OF_SERVICE = `
**Effective Date:** ${EFFECTIVE_DATE}  
**Last Updated:** ${LAST_UPDATED}

**These terms are explicitly designed to be readable by anyone, so please read them!**

## 1. Introduction

Welcome to Rescue Ally. These Terms of Service ("Terms") govern your use of the Rescue Ally mobile application and related services ("Services") operated by Rescue Ally Llc ("we," "our," "us"), a Colorado limited liability company.

By creating an account or using our Services, you agree to these Terms. If you do not agree, please do not use our Services.

## 2. About the Service

Rescue Ally is a field medicine app designed for wilderness medicine practitioners. Its primary function is to help users document patient care in remote and austere environments, generate SOAP Notes, and share records during medical handoffs.

Rescue Ally also includes a wilderness medicine field guide, *The Little Big Book of Wilderness Medicine*, authored and maintained by [Backcountry Pulse](https://backcountrypulse.org), a wilderness medicine education organization. The field guide provides evidence-based reference material for use in the field and is also available as a standalone physical publication.

Rescue Ally includes an optional **Scribe** feature, currently in preview, which transcribes the patient interview on your device and suggests observations for your assessment. Scribe is a documentation aid: it drafts, you decide. AI is not perfect, and you must review and correct every observation before relying on it. Using Scribe requires accepting the in-app [Scribe Usage Agreement](/scribe-agreement), which covers how recordings are handled.

Rescue Ally also includes **practice scenarios**: training exercises built on real incidents, in which you work an invented patient from the scene to the evacuation decision. Names, places, and dialogue are invented. A scenario shows one reasonable way to handle an incident and explains why; it is not a certification course, it does not replace hands-on training, and it does not assess or certify your skills.

**Rescue Ally does not provide medical diagnoses or automated clinical decision support.** The field guide is an educational reference authored by qualified wilderness medicine professionals. All clinical decisions remain the sole responsibility of the treating provider.

## 3. Medical Disclaimer and Intended Use

**Rescue Ally is an educational reference and documentation tool. It is not a medical device, and nothing in this app constitutes medical advice, diagnosis, or treatment.**

The field guide included in Rescue Ally, *The Little Big Book of Wilderness Medicine*, is authored by [Backcountry Pulse](https://backcountrypulse.org), an accredited wilderness medicine education organization. It is reference material, comparable to a textbook. Using it does not create a provider-patient relationship between you and Rescue Ally, Backcountry Pulse, or any individual author.

### Intended use

Rescue Ally is designed for use in **wilderness and austere settings**: environments where geographic or logistical challenges reduce the availability of professional medical resources or delay patient transport.

**This app does not replace professional medical care.** When professional medical services are available and accessible, you should always defer to them. Do not use the field guide as a substitute for calling 911, activating EMS, or seeking hospital care when those options exist.

### Your responsibility

All clinical decisions you make while using this app are **your responsibility**. You agree to:

- **Act within your scope of practice and training.** Do not attempt procedures or interventions you have not been trained to perform. Your wilderness medicine certification level (WFA, WAFA, WFR, WEMT, or equivalent) defines what you are qualified to do.
- **Exercise your own professional judgment.** The field guide provides general reference information. It cannot account for every patient, every situation, or every complication. You must assess each situation independently.
- **Stay current.** Medical best practices evolve. Your training, certifications, and continuing education are your responsibility, not ours.

### Good Samaritan protections (U.S.)

If you provide emergency care in good faith and within the scope of your training, you may be protected by Good Samaritan laws in many U.S. states. These laws generally protect people who voluntarily render aid in emergencies, without compensation, and without gross negligence.

**Good Samaritan protections vary by state and do not apply in all situations.** We encourage you to familiarize yourself with the laws in the jurisdictions where you operate. This section is informational only. It is not legal advice, and Rescue Ally makes no guarantee of legal protection.

*This section reflects U.S. law. If you are outside the United States, different laws and standards may apply. We recommend consulting local legal and medical regulations.*

## 4. Eligibility

You must be at least 18 years old to create an account and use our Services. By using Rescue Ally, you represent that you meet this age requirement.

## 5. Your Account

- You are responsible for maintaining the security of your account credentials and authentication methods (email, phone number, biometric access).
- You are responsible for all activity that occurs under your account.
- You must provide accurate and current information when creating your account.
- You must notify us promptly at [support@rescueally.org](mailto:support@rescueally.org) if you believe your account has been compromised.

For details on how authentication works, including passwordless login via email or SMS, see our [Security](/security) documentation and [SMS Terms and Conditions](/sms).

## 6. Pricing and Payment

Rescue Ally uses a **one-time purchase model**, not a recurring subscription. For current pricing options and details, see our [Pricing](/pricing) page.

### Free Trial

New accounts get a free 24-hour trial of Rescue Ally when they first sign in on the mobile app. There is no payment and no credit card. The 24 hours start when you begin your first patient assessment. The free trial is one per person, so we may not offer it to an account that shares a verified email address, phone number, or device with an account that already has, or has had, access to Rescue Ally. When the trial ends, you need to buy Rescue Ally to keep using it.

### Lifetime Access

Rescue Ally is a new business, and lifetime access may not be offered forever. We may change how we charge for the app as we grow. **What you have already bought is not affected.** If we move to a different pricing model, everyone who purchased lifetime access keeps it: the same app, the same free updates, at no additional cost.

### Payment Processing

Purchases made on our website (at [app.rescueally.org/buy](https://app.rescueally.org/buy), after you sign in) are processed securely by our payment partner, [Stripe](https://stripe.com); by making a purchase there, you also agree to [Stripe's Terms of Service](https://stripe.com/legal/end-users). Purchases made inside the iOS app are processed by Apple through In-App Purchase and are governed by Apple's terms; refunds for those purchases are handled by Apple.

### Monthly Payment Plan

The monthly plan is a payment plan, not a membership or subscription. Choosing the monthly plan commits you to the full purchase price of $95.88 (12 payments of $7.99). If you delete your account before completing all 12 payments, the remaining balance is still due.

### Refunds

We offer full refunds if requested within 14 days of your initial purchase. After 14 days, refund requests are handled on a case-by-case basis. Contact us at [support@rescueally.org](mailto:support@rescueally.org) and we will work with you. If you purchased through Apple In-App Purchase, refunds must be requested from Apple; we cannot issue them on your behalf, but write to us anyway and we will help you get it sorted.

## 7. Acceptable Use

You agree to use Rescue Ally only for its intended purpose: documenting patient care and referencing field medicine guidance in wilderness and austere settings. You agree **not** to:

- Use the Services for any unlawful purpose
- Attempt to gain unauthorized access to our systems or other users' accounts
- Reverse engineer, decompile, or disassemble any part of the application
- Use the Services to store or transmit malicious code
- Resell, redistribute, or sublicense access to the Services
- Reproduce, distribute, or create derivative works from the field guide content
- Use the Services in any way that could damage, disable, or impair our infrastructure

## 8. Patient Data and HIPAA

Rescue Ally is designed to be used in compliance with the Health Insurance Portability and Accountability Act (HIPAA). You are responsible for:

- Ensuring that your use of the app complies with applicable healthcare privacy laws
- Maintaining appropriate safeguards when exporting or sharing patient data
- Using the app's built-in security features (encryption, biometric authentication) as intended

For complete details on how we handle data, see our [Privacy Policy](/privacy) and [Security](/security) documentation.

## 9. SMS Communications

If you choose to use SMS-based authentication, you agree to our [SMS Terms and Conditions](/sms). Providing your phone number is optional, and email-based authentication is always available as an alternative. SMS-based authentication is currently available for U.S. phone numbers only.

## 10. Intellectual Property

All content, features, and functionality of the Rescue Ally app, including the software, design, text, graphics, and field medicine reference materials, are owned by Rescue Ally Llc or licensed from our content partners (including Backcountry Pulse) and protected by applicable intellectual property laws.

Your purchase grants you a personal, non-exclusive, non-transferable license to use the app and its included content. This license does not transfer ownership of any intellectual property to you.

## 11. Disclaimer of Warranties

Rescue Ally is provided **"as is"** and **"as available."** To the fullest extent permitted by law, we disclaim all warranties, express or implied, including but not limited to:

- Merchantability and fitness for a particular purpose
- That the Services will be uninterrupted, error-free, or secure at all times
- That the field medicine reference materials are complete or current for every situation

**You acknowledge that all medical decisions are your responsibility.** See **Section 3 (Medical Disclaimer and Intended Use)** for important details about the limitations of this app and your obligations as a user.

## 12. Limitation of Liability

To the maximum extent permitted by law, Rescue Ally Llc and its officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services.

Our total aggregate liability for any claim arising from or related to these Terms or the Services shall not exceed the amount you paid to us in the 12 months preceding the claim.

## 13. Indemnification

You agree to indemnify and hold harmless Rescue Ally Llc, its officers, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:

- Your use of the Services
- Your violation of these Terms
- Your violation of any applicable law or regulation
- Any patient data you enter, export, or share using the app

## 14. Service Modifications

We may update, modify, or discontinue features of the Services from time to time. We will make reasonable efforts to notify you of material changes. Your continued use of the Services after changes take effect constitutes acceptance of those changes.

## 15. Account Termination

- **By you:** You may delete your account at any time through the app, through the [Privacy Center](https://app.rescueally.org/privacy-center) (where you can also download a copy of your data, or delete only your assessment data), or by contacting [support@rescueally.org](mailto:support@rescueally.org). Patient health information will be retained as required by HIPAA.
- **By us:** We may suspend or terminate your account if you violate these Terms or engage in activity that threatens the security or integrity of our Services. We will provide notice when reasonably possible.

## 16. Governing Law

These Terms are governed by and construed in accordance with the laws of the State of Colorado, without regard to conflict of law principles. Any disputes arising from these Terms shall be resolved in the courts of the State of Colorado, County of Broomfield.

## 17. Changes to These Terms

We may update these Terms from time to time. We will notify you of material changes via email or in-app notification before they take effect. Continued use of the Services after the updated Terms take effect constitutes your acceptance.

## 18. Related Policies

- [Privacy Policy](/privacy)
- [Security](/security)
- [SMS Terms and Conditions](/sms)
- [Scribe Usage Agreement](/scribe-agreement)
- [Pricing](/pricing)

## 19. Contact Us

**Rescue Ally Support**  
Email: [support@rescueally.org](mailto:support@rescueally.org)  
Phone: +1 (303)-578-8161

---

_These terms are designed to be clear and straightforward. If you have questions, please reach out. We're happy to help._
`;
